import axios from 'axios';
import { EMPTY_TIMESTAMP } from 'constants/index';
import FileSaver from 'file-saver';
import apiClient from 'lib/apiClient';
import { DATE_FORMAT, toUserTimezone } from 'utils';

const client = axios.create({
  baseURL: '',
});

export const getContacts = (query) => {
  return apiClient
    .get('API/1_0/contact.php', {
      params: query,
    })
    .then((result) => {
      if (query.exportCSV) return result;
      result.forEach((contact) => {
        contact.groups = contact.groups?.filter((g) => g);
        contact.groups?.forEach((g) => {
          const contactGroup = contact.group_contacts?.find(
            (i) => i.group_id === g.group_id,
          );
          if (contactGroup) {
            g.group_contact_create_date =
              contactGroup.group_contact_create_date;
          }
        });
      });
      return result;
    });
};

export const getContact = (query) => {
  return apiClient
    .get('API/1_0/contact.php', { params: query })
    .then((r) => r[0]);
};

export const getContactWithGroups = async (query) => {
  const contact = await getContact({ ...query, load_groups: 1 });
  if (!contact) {
    throw new Error('Contact not found');
  }
  const groups = contact.groups.filter((g) => g);
  contact.groups = groups;
  if (groups.length) {
    contact.groups.forEach((g) => {
      const contactGroup = contact.group_contacts?.find(
        (i) => i.group_id === g.group_id,
      );
      if (contactGroup) {
        g.group_contact_create_date = contactGroup.group_contact_create_date;
      }
    });
  }
  return contact;
};

export const createContact = (data) => {
  return apiClient.post('API/1_0/contact.php', data);
};

export const updateContact = (id, data) => {
  data.hash = id;
  return apiClient.post('API/1_0/contact.php', data);
};

export const deleteContact = (id) =>
  apiClient.delete(`API/1_0/contact.php`, { params: { hash: id } });

export const getGroupsForContact = async (hash) => {
  const groups = await apiClient.get('API/1_0/group.php', {
    params: { contact_hash: hash },
  });
  if (groups.length) {
    const contactGroups = await apiClient.get('API/1_0/groupContact.php', {
      params: {
        contact_hash: hash,
      },
    });
    groups.forEach((g) => {
      const contactGroup = contactGroups.find((i) => i.group_id === g.group_id);
      if (contactGroup) {
        g.group_contact_create_date = contactGroup.group_contact_create_date;
      }
    });
  }
  return groups;
};

export const addContactToGroup = (
  contactHash,
  groupHash,
  notifyowner = 0,
  notifycontact = 0,
  source = 'Software',
) =>
  apiClient.post('API/1_0/groupContact.php', {
    group_hash: groupHash,
    contact_hash: contactHash,
    group_contact_source: source,
    notifyowner,
    notifycontact,
  });

export const removeContactFromGroup = (contactHash, groupHash) =>
  apiClient.delete('API/1_0/groupContact.php', {
    params: {
      group_hash: groupHash,
      contact_hash: contactHash,
    },
  });

export const getContactGroupCount = (contactHash) =>
  apiClient.get('API/1_0/getGroupContactCount.php', {
    params: { contact_hash: contactHash },
  });

export const bulkAddToGroup = (contactsId, groupIdHash) => {
  return Promise.all(
    contactsId.map((cid) => addContactToGroup(cid, groupIdHash)),
  );
};

export const bulkRemoveFromGroup = (contactsId, groupIdHash) => {
  return Promise.all(
    contactsId.map((cid) => removeContactFromGroup(cid, groupIdHash)),
  );
};

export const bulkDelete = (contactsHashes) => {
  return Promise.all(contactsHashes.map((cid) => deleteContact(cid)));
};

export const bulkRestore = (contactsHashes) => {
  return Promise.all(
    contactsHashes.map((id) => updateContact(id, { contact_active: '1' })),
  );
};

export const bulkExportSelected = (contacts, account, groupsColumn) => {
  const data = contacts.map((c) => {
    const d = {
      'Contact Hash': c.hash,
      'First Name': c.contact_first_name || '',
      'Last Name': c.contact_last_name || '',
      'Secondary Phone': c.secondary_phone || '',
      'Primary Phone': c.primary_phone || '',
      Email: c.contact_email || '',
      'Address 1': c.contact_address1 || '',
      'Address 2': c.contact_address2 || '',
      City: c.contact_city || '',
      State: c.contact_state || '',
      Zip: c.contact_zip || '',
      'Date Added': toUserTimezone(
        c.contact_create_date,
        account.account_timezone,
      ).format(DATE_FORMAT),
      'Email Opt Out Date':
        c.opted_out_email_date !== EMPTY_TIMESTAMP &&
        toUserTimezone(c.opted_out_email_date, account.account_timezone).format(
          DATE_FORMAT,
        ),
      'SMS Opt Out Date':
        c.opted_out_sms_date !== EMPTY_TIMESTAMP &&
        toUserTimezone(c.opted_out_sms_date, account.account_timezone).format(
          DATE_FORMAT,
        ),
    };
    if (groupsColumn) {
      d.Groups = c.groups ? c.groups.map((g) => g.group_name).join(', ') : '';
    }
    return d;
  });
  return apiClient
    .post('API/1_0/postCSVfromJSON.php', { rows: data })
    .then((csv) => {
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      FileSaver.saveAs(blob, 'people_export.csv');
    });
};

export const bulkExport = (csv) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  FileSaver.saveAs(blob, 'people_export.csv');
};

export const socialSync = (query) =>
  client.get('/api/fullcontact/person', { params: query }).then((r) => r.data);

export const mergeContacts = (contact1, contact2, props) =>
  apiClient.post('API/1_0/postContactMerge.php', {
    contact_hash_1: contact1,
    contact_hash_2: contact2,
    ...props,
  });

export const getComments = (contactHash) =>
  apiClient.get('API/1_0/contactComment.php', {
    params: { contact_hash: contactHash },
  });

export const saveComment = (data) =>
  apiClient.post('API/1_0/contactComment.php', data);

export const deleteComment = (hash) =>
  apiClient.delete('API/1_0/contactComment.php', { params: { hash } });

export const deleteContactData = (contact_hash) =>
  apiClient.post('API/1_0/postDelContactData.php', { contact_hash });

export const getPayments = (contact_hash) =>
  apiClient.get('API/1_0/contactPayment.php', {
    params: { contact_hash },
  });

export const savePayment = (data) =>
  apiClient.post('API/1_0/contactPayment.php', data);

export const deletePayment = (hash) =>
  apiClient.delete('API/1_0/contactPayment.php', { params: { hash } });

export const getCustomColumnValues = (contact_hash, account_hash) =>
  apiClient.get('API/1_0/contactCustomColumns.php', {
    params: { contact_hash, account_hash },
  });

export const saveCustomColumnValue = (values) =>
  apiClient.post('API/1_0/contactCustomColumnsValues.php', values);

export const deleteCustomColumnValue = (hash) =>
  apiClient.delete('API/1_0/contactCustomColumnsValues.php', {
    params: { hash },
  });

export const getContactStats = () =>
  apiClient.get('API/1_0/getContactStats.php', {});

export const getTabSections = (params) => {
  return apiClient.get('API/1_0/contactTabSection.php', { params });
};

export const saveTabSections = (data) =>
  apiClient.post('API/1_0/contactTabSection.php', data);

export const deleteTabSection = (hash) =>
  apiClient.delete('API/1_0/contactTabSection.php', { params: { hash } });

export const getTabSectionFields = (accountId) => {
  return apiClient.get('API/1_0/contactTabSectionToCustomColumns.php', {
    params: { account_id: accountId },
  });
};

export const saveTabSectionFields = (data) =>
  apiClient.post('API/1_0/contactTabSectionToCustomColumns.php', data);

export const deleteTabSectionField = (hash) =>
  apiClient.delete('API/1_0/contactTabSectionToCustomColumns.php', {
    params: { hash },
  });

export const getContactLogs = (contact_id) =>
  apiClient.get('API/1_0/contactLog.php', {
    params: { contact_id },
  });

export const importContacts = (data) => {
  if (data.file) {
    const formData = new FormData();
    formData.append('file', data.file, data.file.name);
    formData.append('action', 'upload');
    return apiClient.post(`API/1_0/postImport.php`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }
  return apiClient.post('API/1_0/postImport.php', data);
};

export const getHousehold = (contactId) =>
  apiClient.get('API/1_0/household.php', {
    params: { contact_id: contactId, load_household_contacts: 1 },
  });

export const createHousehold = (name) =>
  apiClient.post('API/1_0/household.php', { household_name: name });

export const updateHousehold = (hash, name) =>
  apiClient.post('API/1_0/household.php', { hash, household_name: name });

export const addHouseholdMember = (params) =>
  apiClient.post('API/1_0/householdContact.php', params);

export const removeHouseholdMember = (hash) =>
  apiClient.delete('API/1_0/householdContact.php', { params: { hash } });

export const contactActions = async (contact_hash, query = {}) => {
  return await apiClient.get('API/1_0/contactAction.php', {
    params: { contact_hash, ...query },
  });
};

// Background Check API
export const getBackgroundCheck = ({ contact_id, background_check_id }) => {
  return apiClient.get('API/1_0/backgroundChecks.php', {
    params: contact_id ? { contact_id } : { background_check_id },
  });
};

export const updateBackgroundCheckStatus = ({ hash, background_check_status, background_check_comment }) => {
  return apiClient.post('API/1_0/backgroundChecks.php', {
    hash,
    background_check_status,
    background_check_comment,
  });
};

export const orderBackgroundCheck = ({ contact_id }) => {
  return apiClient.post('API/1_0/postBackgroundCheckOrder.php', {
    contact_id,
  });
};
